import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { SpanCol } from "skedyul-ui/ui";

/**
 * Agent connection schema - now shows a message that agent connections are configured via YAML.
 * Agent-to-agent routing is defined in the agent's config at runtime, not from database bindings.
 */
export const addAgentToolConnectionSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const {
    agents: {
      flow: { scopedAgentId },
      agents: { data: agents },
    },
    sideNavigation: {
      right: { isOpen, params },
    },
  } = args.state;

  const sourceAgentId =
    scopedAgentId || (params?.sourceAgentId as string | undefined);
  const sourceAgent = agents.find((a) => a.id === sourceAgentId);
  const sourceAgentName =
    sourceAgent?.name || (params?.sourceAgentName as string | undefined);

  const targetAgentName = params?.targetAgentName as string | undefined;

  return {
    side: "right",
    isOpen,
    width: "500px",
    component: {
      component: "PageV2",
      props: {
        title: "Agent Connection Configuration",
        hideTitle: true,
        header: {
          icon: "GitBranch",
          primaryAction: {
            isLoading: false,
            label: "Close",
            handleClick: () => {
              store.dispatch(closeSideNavigation({ position: "right" }));
            },
          },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Agent Connection Configuration",
              props: {
                header: {
                  title: `Connection from ${sourceAgentName || "Agent"}`,
                  description: targetAgentName
                    ? `To ${targetAgentName}`
                    : "Agent connections are configured via YAML",
                },
                form: {
                  formVersion: "v1",
                  id: "agent-connection-info-form",
                  fields: [
                    {
                      component: "Textarea" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "connection-info",
                        label: "Configuration",
                        value: `Agent-to-agent connections are defined in the agent's YAML configuration file.

To add agent routing:
1. Edit the agent's YAML file
2. Add the target agent to the 'tools' array using @agent/ prefix
3. Deploy the updated configuration

Example:
tools:
  - tool: "@agent/sales-specialist"
    name: Route to Sales
    description: Hand off to sales specialist for pricing questions`,
                        isDisabled: true,
                        rows: 14,
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  };
};
